import React from 'react';
import { Check, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mock';

const AggressivePricing = () => {
  return (
    <section className="py-20 bg-jet-black relative overflow-hidden">
      {/* Background Energy Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-grid-pattern opacity-5"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-primary opacity-10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="heading-power mb-6 animate-slide-power">
            STOP MAKING <span className="text-neon-green">EXCUSES</span>
          </h2>
          <p className="text-energy max-w-3xl mx-auto animate-slide-power delay-200">
            Your transformation has a price. Your mediocrity has a higher one. 
            Choose your investment wisely.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {mockData.pricingPlans.map((plan, index) => (
            <div 
              key={plan.id}
              className={`relative group animate-slide-power hover-lift ${
                plan.popular 
                  ? 'lg:scale-110 z-10' 
                  : 'lg:hover:scale-105'
              }`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-20">
                  <div className="px-8 py-3 rounded-full shadow-lg" style={{background: 'linear-gradient(135deg, #007BFF 0%, #39FF14 100%)'}}>
                    <div className="flex items-center space-x-2 whitespace-nowrap">
                      <Zap className="w-4 h-4 text-white" />
                      <span className="text-white font-bold text-sm uppercase tracking-wide">MOST POPULAR</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Card Container */}
              <div className={`relative bg-black rounded-3xl p-8 transition-all duration-300 ${
                plan.popular 
                  ? 'neon-border bg-gradient-to-b from-gray-900/50 to-black' 
                  : 'border-2 border-gray-800 hover:border-electric-blue/50'
              }`}>
                
                {/* Glow Effect for Popular Plan */}
                {plan.popular && (
                  <div className="absolute inset-0 bg-gradient-primary opacity-5 rounded-3xl"></div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-8 relative z-10">
                  <h3 className="heading-bold text-white mb-4 uppercase tracking-wider">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-6xl font-black text-white">${plan.price}</span>
                    <span className="text-gray-400 ml-2 uppercase tracking-wide">/{plan.period}</span>
                  </div>
                  {plan.price === 0 && (
                    <p className="text-neon-green font-bold uppercase tracking-wide">NO RISK. NO EXCUSES.</p>
                  )}
                </div>

                {/* Features List */}
                <ul className="space-y-4 mb-8 relative z-10">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start group-item">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 transition-all duration-300 ${
                        plan.popular 
                          ? 'bg-gradient-primary glow-blue' 
                          : 'bg-gray-800 group-hover:bg-electric-blue'
                      }`}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className={`text-standard transition-colors duration-300 ${
                        plan.popular 
                          ? 'text-gray-200' 
                          : 'text-gray-400 group-hover:text-gray-200'
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <div className="relative z-10">
                  {plan.popular ? (
                    <button className="w-full btn-power py-4 text-lg animate-pulse-power">
                      {plan.price === 0 ? 'START FREE NOW' : 'GET STARTED'}
                    </button>
                  ) : (
                    <button className="w-full btn-secondary py-4 text-lg hover:bg-electric-blue hover:border-electric-blue">
                      {plan.price === 0 ? 'TRY FREE' : 'CHOOSE PLAN'}
                    </button>
                  )}
                </div>

                {/* Subtle Energy Animation for Popular Plan */}
                {plan.popular && (
                  <>
                    <div className="absolute -inset-1 border border-neon-green/10 rounded-3xl animate-pulse opacity-30" style={{animationDuration: '3s'}}></div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Motivation */}
        <div className="text-center mt-16 animate-slide-power delay-500">
          <div className="inline-block bg-gradient-to-r from-electric-blue/20 to-neon-green/20 rounded-2xl p-8 neon-border">
            <h3 className="heading-bold text-white mb-4">
              <span className="text-neon-green">NO EXCUSES.</span> START TODAY.
            </h3>
            <p className="text-energy mb-6">
              Every day you wait is another day you stay the same. Your future self is counting on you.
            </p>
            <p className="text-standard text-gray-400">
              14-day free trial • No credit card required • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AggressivePricing;